import { useEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { Chip } from '@mui/material';
import PropTypes from 'prop-types';

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import CheckIcon from '@mui/icons-material/Check';

import ExperienceWalletCustomAutocomplete from './ExperienceWalletCustomAutocomplete';

/* Redux */
import { useDispatch, useSelector } from 'react-redux';
import { setCareers } from 'app/slices/careersSlice';

/* API */
import { useCareers, useMilitaryCareers } from 'lib/hooks/careersQueries';

const MIN_AUTO_MATCH_LENGTH = 4;

export default function ExperienceWalletFieldsAutocomplete({
  isDisabled,
  value,
  onChange,
  error,
  helperText,
  open,
  setOpen,
  id,
  initialInputValue,
  onAutoMatch,
  isMilitary,
}) {
  const intl = useIntl();
  const careersQuery = useCareers();
  const militaryCareersQuery = useMilitaryCareers();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');

  const { results = [] } = useSelector((state) => state.careers);
  const militaryResults = militaryCareersQuery.data || [];
  const rawOptions = isMilitary ? militaryResults : results;
  // De-duplicate by SOC code. The military SOC feed can return repeated rows
  // (the same SOC/title more than once), which would otherwise surface as
  // duplicate React keys in the dropdown.
  const options = useMemo(() => {
    const seen = new Set();
    return rawOptions.filter((o) => {
      if (seen.has(o.soc)) return false;
      seen.add(o.soc);
      return true;
    });
  }, [rawOptions]);
  const lastMatchedTitle = useRef('');
  const onAutoMatchRef = useRef(onAutoMatch);

  useEffect(() => {
    onAutoMatchRef.current = onAutoMatch;
  }, [onAutoMatch]);

  useEffect(() => {
    if (careersQuery.isSuccess) {
      dispatch(setCareers(careersQuery.data));
    }
  }, [careersQuery.isSuccess, careersQuery.data, careersQuery.status]);

  // Sync searchText when value changes externally (auto-match or selection)
  useEffect(() => {
    if (value) {
      setSearchText(value.title);
    } else {
      setSearchText('');
    }
  }, [value]);

  // When job title changes, find best matching occupation and auto-select
  useEffect(() => {
    if (
      initialInputValue &&
      initialInputValue.length >= MIN_AUTO_MATCH_LENGTH &&
      options.length > 0 &&
      !value &&
      lastMatchedTitle.current !== initialInputValue
    ) {
      lastMatchedTitle.current = initialInputValue;
      const titleLower = initialInputValue.toLowerCase();
      // Find exact match first, then full word match
      const escapedTitle = titleLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wholeWordRegex = new RegExp(`\\b${escapedTitle}\\b`, 'i');
      const prefixRegex = new RegExp(`\\b${escapedTitle}`, 'i');
      const match =
        options.find((r) => r.title.toLowerCase() === titleLower) ||
        options.find((r) => wholeWordRegex.test(r.title)) ||
        options.find((r) => prefixRegex.test(r.title));
      if (match) {
        setSearchText(match.title);
        if (onAutoMatchRef.current) {
          onAutoMatchRef.current(match);
        }
      }
    }
  }, [initialInputValue, options, value]); // eslint-disable-line

  return (
    <ExperienceWalletCustomAutocomplete
      disableClearable
      options={options}
      inputValue={searchText}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setSearchText(newInputValue);
          // If user types something different from selected value, clear selection so they can search
          if (value && newInputValue !== value.title) {
            onChange(null, null);
          }
        } else if (reason === 'clear') {
          setSearchText('');
        } else if (reason === 'reset' && value) {
          // MUI fires this 'reset' during its own render to resync the
          // controlled inputValue to the selected option's label. Defer the
          // state update so we don't call setState while Autocomplete is
          // rendering ("Cannot update a component while rendering a different
          // component"). Behavior is unchanged — the input still shows the
          // selected title.
          queueMicrotask(() => setSearchText(value.title));
        }
      }}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, value) => {
        return option.soc === value?.soc;
      }}
      disabled={isDisabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={onChange}
      sx={{
        '& .MuiInputBase-root:not(.Mui-disabled)': {
          backgroundColor: 'white',
        },
        '& .MuiInputBase-root': {
          paddingTop: '10px!important',
          paddingBottom: '2px!important',
        },
        '& .MuiFilledInput-input': {
          fontSize: 12,
        },
      }}
      renderOption={(props, option, { selected }) => {
        // Override MUI's default option key (derived from the title) with the
        // unique SOC code so duplicate titles across SOCs don't collide.
        const { key, ...optionProps } = props; // eslint-disable-line no-unused-vars
        return (
          <div
            {...optionProps}
            key={option.soc}
            style={{ display: 'flex', alignItems: 'center', margin: '2px 0', position: 'relative' }}
          >
            {selected ? <CheckIcon fontSize="small" style={{ position: 'absolute', left: 11 }} /> : null}
            <span style={{ paddingLeft: 20 }}>{option.title}</span>
          </div>
        );
      }}
      renderTags={(value, getTagProps) => (
        <div style={{ width: '100%' }}>
          {value.map((option, index) => {
            const { className, ...rest } = getTagProps({ index }); //eslint-disable-line
            return (
              <Chip
                key={option.soc}
                size="small"
                sx={{ mr: 1, mt: 1 }}
                color="primary"
                label={option.title}
                {...rest}
              />
            );
          })}
        </div>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          placeholder={intl.formatMessage({ id: 'experience.form.fieldsPlaceholder' })}
          inputProps={{
            ...params.inputProps,
            'aria-label': 'fields',
            id,
          }}
          helperText={helperText}
          sx={{
            '& input::placeholder': {
              color: 'rgba(0,0,0,0.87)',
              opacity: 1,
            },
          }}
          error={error}
        />
      )}
    />
  );
}

ExperienceWalletFieldsAutocomplete.propTypes = {
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.object,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  id: PropTypes.string,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  initialInputValue: PropTypes.string,
  onAutoMatch: PropTypes.func,
  isMilitary: PropTypes.bool,
};
